import FormDialog, { NEW_PREFIX } from "./FormDialog";
import type { Contact, Org, Partner } from "../types";
import { useData } from "../hooks/useData";
import { api } from "../api/client";

interface Options { newsletters: { id: string; label: string }[]; languages: { id: string; label: string }[] }

/**
 * Add or edit a contact: who they are, how to reach them, the language to write to them in,
 * who at Saga owns the relationship, which newsletters they get — and notes.
 */
export default function ContactForm({ contact, account, orgs, partners, onClose, onSaved }: {
  contact: Contact | null;                       // null = new contact
  account?: { id: string | null; name: string } | null;   // prefilled for a new contact
  orgs: Org[];
  partners: Record<string, Partner>;
  onClose: () => void;
  onSaved: (c: Contact) => void;
}) {
  const { data: opts } = useData<Options>("/api/contact-options");
  const { data: markets } = useData<{ name: string; flag: string | null }[]>("/api/markets");
  const c = contact;
  const accountValue = c ? (c.org_id || (c.org_name ? NEW_PREFIX + c.org_name : "")) : account?.id || (account?.name ? NEW_PREFIX + account.name : "");
  const unsubscribed = c?.unsubscribed_at && !(c.newsletters || []).length;

  return (
    <FormDialog
      title={c ? `Edit ${c.name}` : "New contact"}
      sub={c?.org_name || account?.name || undefined}
      initial={{
        name: c?.name || "", title: c?.title || "", account: accountValue,
        email: c?.email || "", phone: c?.phone || "", mobile: c?.mobile || "", linkedin_url: c?.linkedin_url || "",
        market: c?.market || "", language: c?.language || "",
        relationship_owners: (c?.relationship_owners || []).join(","),
        newsletters: (c?.newsletters || []).join(","),
        do_not_email: c?.do_not_email ? "yes" : "no",
        how_we_know: c?.how_we_know || "", notes: c?.notes || "",
      }}
      fields={[
        { key: "name", label: "Name", required: true },
        { key: "title", label: "Title / role", placeholder: "e.g. Director, Upstream" },
        { key: "account", label: "Account", type: "select", wide: true, allowNew: "New account…",
          options: [...orgs].sort((a, b) => a.name.localeCompare(b.name)).map(o => ({ value: o.id, label: o.name })) },
        { key: "email", label: "Email", placeholder: "name@company.com" },
        { key: "phone", label: "Phone" },
        { key: "mobile", label: "Mobile / WhatsApp" },
        { key: "linkedin_url", label: "LinkedIn", placeholder: "linkedin.com/in/…" },
        { key: "market", label: "Country", type: "select", allowNew: "Other country…",
          options: (markets || []).map(m => ({ value: m.name, label: `${m.flag ? m.flag + " " : ""}${m.name}` })) },
        { key: "language", label: "Writes in", type: "select",
          options: (opts?.languages || []).map(l => ({ value: l.id, label: l.label })) },
        { key: "relationship_owners", label: "Relationship owners", type: "people",
          options: Object.values(partners).filter(p => p.active !== false).map(p => ({ value: p.id, label: p.name.split(" ")[0] })) },
        { key: "newsletters", label: "Newsletters", type: "people",
          options: (opts?.newsletters || []).map(n => ({ value: n.id, label: n.label })) },
        { key: "do_not_email", label: "May we email them?", type: "select", required: true,
          options: [{ value: "no", label: "Fine to email" }, { value: "yes", label: "Do not email (no newsletters, bots won't send)" }] },
        { key: "how_we_know", label: "How we know them", wide: true, placeholder: "e.g. Met at AEW Cape Town 2026 · introduced by Trond" },
        { key: "notes", label: "Notes", type: "textarea" },
      ]}
      saveLabel={c ? "Save" : "Add contact"}
      onClose={onClose}
      onSave={async v => {
        // A new account typed in the picker becomes a real account first.
        let orgId: string | null = v.account && !v.account.startsWith(NEW_PREFIX) ? v.account : null;
        let orgName = orgId ? orgs.find(o => o.id === orgId)?.name || null : null;
        if (v.account.startsWith(NEW_PREFIX) && v.account.slice(NEW_PREFIX.length).trim()) {
          const org = await api.post<Org>("/api/orgs", { name: v.account.slice(NEW_PREFIX.length).trim(), type: "Other" });
          orgId = org.id; orgName = org.name;
        }
        const market = v.market.startsWith(NEW_PREFIX) ? v.market.slice(NEW_PREFIX.length).trim() : v.market;
        const body = {
          name: v.name, title: v.title, email: v.email, phone: v.phone, mobile: v.mobile, linkedin_url: v.linkedin_url,
          market, language: v.language, relationship_owners: v.relationship_owners, how_we_know: v.how_we_know, notes: v.notes,
          do_not_email: v.do_not_email === "yes", org_id: orgId, org_name: orgName,
          // Only send the newsletter choice if it changed, so an unsubscribed person isn't touched by an unrelated edit.
          ...(v.newsletters !== (c?.newsletters || []).join(",") ? { newsletters: v.newsletters ? v.newsletters.split(",") : [] } : {}),
        };
        const saved = c ? await api.put<Contact>(`/api/contacts/${c.id}`, body) : await api.post<Contact>("/api/contacts", body);
        onSaved(saved);
      }}
    >
      <div className="cf-hint">
        {unsubscribed
          ? <>Unsubscribed on {String(c!.unsubscribed_at).slice(0, 10)} — only they can sign up again.</>
          : <>Ticking a newsletter records that they're an existing business contact you added; they can unsubscribe from any issue.</>}
      </div>
    </FormDialog>
  );
}
